import { FilledButton, OutlinedButton } from "@/components/ui/buttons";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { differenceInYears } from "date-fns";
import { useCloseOnBackButton } from "@/hooks/ui/use-close-on-back-button";
import { memo } from "react";

interface BirthdayConfirmationModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  birthday: string;
  onConfirm: () => void;
  onEdit?: () => void;
}

export const BirthdayConfirmationModal = memo(function BirthdayConfirmationModal({
  isOpen,
  onDismiss,
  birthday,
  onConfirm,
  onEdit,
}: BirthdayConfirmationModalProps) {
  useCloseOnBackButton(isOpen, onDismiss);

  if (!isOpen) return null;

  const age = (() => {
    try {
      return differenceInYears(new Date(), new Date(birthday));
    } catch {
      return 0;
    }
  })();

  return (
    // Fixed overlay rendered inside the React root (not an IonModal portal), so
    // React synthetic events work normally. z-[1100] puts it above ion-modal (1001).
    <div className="fixed inset-0 z-[1100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onDismiss} />
      <div className="animate-birthday-dialog-enter relative z-10 mx-auto w-[90%] max-w-[321px] overflow-hidden rounded-[32px] border border-[#e9e9e9] bg-white shadow-[0px_0px_30px_0px_rgba(0,0,0,0.25)]">
        <div className="flex flex-col items-center p-4 pt-0">
          <div className="relative h-[186px] w-[216px] bg-[url('/assets/images/dots-bg.svg')] bg-center bg-no-repeat">
            <div className="absolute top-1/2 left-1/2 flex h-[84px] w-[84px] -translate-x-1/2 -translate-y-1/4 items-center justify-center rounded-full border-2 border-white bg-[#f0e4ff] shadow-[0px_0px_12px_0px_rgba(0,0,0,0.12)]">
              <img
                src="/assets/images/confetti.svg"
                alt="Celebration"
                className="relative z-10 h-12 w-12"
              />
            </div>
          </div>

          <h2 className={cn(textVariants.title2, "mb-4 text-center")}>Woo-hoo!</h2>

          <p className={cn(textVariants.bodySingleLine, "mb-6")}>You're {age}!</p>

          <div className="w-full space-y-4">
            <FilledButton text="Thanks!" color="brand" onClick={onConfirm} />
            {onEdit && (
              <OutlinedButton
                textCase="normal"
                className="border-[#ebe0fb] bg-white text-[#620ad5] hover:bg-white active:bg-white"
                onClick={onEdit}
              >
                Edit
              </OutlinedButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});
